import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactCTA from '../components/ContactCTA';
import { Calendar, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { blogCollectionSchema, companySchema, getPageMetadata } from '../config/seo';

const BlogPage = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const meta = getPageMetadata('blog', language);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?published=true`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  // Get title based on language, with fallback
  const getPostTitle = (post) => {
    if (language === 'en') return post.title_en || post.title_tc;
    if (language === 'cn') return post.title_cn || post.title_tc;
    return post.title_tc;
  };

  const getPostContent = (post) => {
    if (language === 'en') return post.content_en || post.content_tc;
    if (language === 'cn') return post.content_cn || post.content_tc;
    return post.content_tc;
  };

  return (
    <div className="min-h-screen">
      <SEO {...meta} path="/blog" language={language} schemas={[companySchema(), blogCollectionSchema(posts, language)]} />
      <Header />

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white" data-testid="blog-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold mb-6 gradient-text" data-testid="blog-title">
              {language === 'tc' ? '最新資訊' : language === 'cn' ? '最新资讯' : 'Latest News'}
            </h1>
            <p className="text-xl text-slate-600">
              {language === 'tc' ? '了解我們的最新動態和ESG創新' : language === 'cn' ? '了解我们的最新动态和ESG创新' : 'Stay updated with our latest news and ESG innovations'}
            </p>
          </motion.div>

          {posts.length === 0 ? (
            <div className="text-center text-slate-600 py-16">
              <p className="text-xl">{language === 'tc' ? '目前沒有文章' : language === 'cn' ? '目前没有文章' : 'No articles available'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.post_id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                  data-testid={`blog-post-${index}`}
                >
                  {post.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={getPostTitle(post)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center text-sm text-slate-500 mb-3">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(post.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'zh-HK')}
                    </div>
                    <h3 className="text-2xl font-bold mb-3 line-clamp-2">{getPostTitle(post)}</h3>
                    <div 
                      className="text-slate-600 line-clamp-3 mb-4"
                      dangerouslySetInnerHTML={{ __html: getPostContent(post) }}
                    />
                    <Link 
                      to={`/blog/${post.post_id}`}
                      className="inline-flex items-center text-primary font-semibold hover:gap-2 transition-all"
                    >
                      {language === 'tc' ? '閱讀更多' : language === 'cn' ? '阅读更多' : 'Read More'}
                      <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <ContactCTA />

      <Footer />
    </div>
  );
};

export default BlogPage;